import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import styled from "styled-components/macro";
import { useHistory, useParams } from "react-router-dom";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";
import { Autocomplete } from "@material-ui/lab";
import Dialog from "../shared/Dialog";
import Timestamps from "../Timestamps";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import { useModals } from "../../hooks/useModals";
import Img from "../shared/Image";

const Section = styled(Paper)`
  padding: 16px;
`;

const CompanyCard = styled(Box)`
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageGalleryEditor = () => {
  const history = useHistory();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const modals = useModals();
  const {
    fetchMyAuthCompanies,
    isMyAuthCompaniesLoading,
    listImageGalleries,
    listGalleryImages,
    updateImageGallery,
    listImageFields,
  } = useAppContext();

  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [companiesLocal, setCompaniesLocal] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isGalleryImagesLoading, setIsGalleryImagesLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [initialCompanyDetails, setInitialCompanyDetails] = useState([]);
  const [fieldsOptions, setFieldsOptions] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [initialFieldIds, setInitialFieldIds] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewRetryingById, setPreviewRetryingById] = useState({});

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const list = await fetchMyAuthCompanies?.();
        if (mounted) setCompaniesLocal(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load my auth companies", e);
      }
    };
    if (!companiesLocal?.length) load();
    return () => {
      mounted = false;
    };
  }, [fetchMyAuthCompanies]);

  useEffect(() => {
    let mounted = true;
    const loadFields = async () => {
      try {
        const list = await listImageFields?.();
        if (mounted) setFieldsOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load fields", e);
      }
    };
    loadFields();
    return () => {
      mounted = false;
    };
  }, [listImageFields]);

  useEffect(() => {
    const set = new Set((initialFieldIds || []).map(String));
    const preselected = (fieldsOptions || []).filter((f) =>
      set.has(String(f.id))
    );
    setSelectedFields(preselected);
  }, [fieldsOptions, initialFieldIds]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoading(true);
      try {
        const list = await listImageGalleries();
        const g = (Array.isArray(list) ? list : []).find(
          (x) => String(x.id) === String(id)
        );
        if (!mounted) return;

        if (g) {
          setName(g.name || "");
          setDescription(g.description || "");
          setIsPublished(g.is_published ?? g.isPublished ?? false);

          // history fields
          setCreatedBy(g.created_by ?? g.createdBy ?? "");
          setCreatedAt(g.created_at ?? g.createdAt ?? null);
          setUpdatedBy(g.updated_by ?? g.updatedBy ?? "");
          setUpdatedAt(g.updated_at ?? g.updatedAt ?? null);

          // Canonical company structure comes from unified `companies: [{ id, accessType }]`.
          // We no longer read legacy arrays `company_ids` / `access_types`.
          const compArr = Array.isArray(g.companies)
            ? g.companies.map((c) => ({
                id: c.id,
                accessType: c.accessType || "read",
              }))
            : [];

          setCompanyDetails(compArr.map((c) => ({ ...c })));
          setInitialCompanyDetails(compArr.map((c) => ({ ...c })));

          const fids =
            g.field_ids ||
            g.fieldIds ||
            (Array.isArray(g.fields) ? g.fields.map((f) => f.id) : []);
          setInitialFieldIds(Array.isArray(fids) ? fids : []);
        } else {
          enqueueSnackbar("Gallery not found", {
            variant: "warning",
            autoHideDuration: 3000,
          });
        }
      } catch (e) {
        console.error("Failed to load gallery", e);
        enqueueSnackbar(e?.message || "Failed to load gallery", {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [id, enqueueSnackbar, listImageGalleries, companiesLocal]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsGalleryImagesLoading(true);
      setPreviewRetryingById({});
      try {
        const images = await listGalleryImages({ galleryId: id });
        if (mounted) setGalleryImages(Array.isArray(images) ? images : []);
      } catch (e) {
        console.error("Failed to load gallery images", e);
        if (mounted) setGalleryImages([]);
        enqueueSnackbar(e?.message || "Failed to load gallery images", {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        if (mounted) setIsGalleryImagesLoading(false);
      }
    };
    if (id) load();
    return () => {
      mounted = false;
    };
  }, [id, enqueueSnackbar, listGalleryImages]);

  const handleSaveMeta = useCallback(async () => {
    setIsSaving(true);
    try {
      const currentMap = new Map(companyDetails.map((c) => [c.id, c]));

      const removeCompanies = [];

      for (const comp of initialCompanyDetails) {
        if (!currentMap.has(comp.id)) removeCompanies.push(comp.id);
      }

      // Only send companies newly added or with changed accessType
      const initialAccessById = new Map(
        (initialCompanyDetails || []).map((c) => [c.id, c.accessType || "read"])
      );
      const addCompanies = companyDetails
        .filter((c) => {
          const prev = initialAccessById.get(c.id);
          const curr = c.accessType || "read";
          return typeof prev === "undefined" || prev !== curr;
        })
        .map((c) => ({ id: c.id, accessType: c.accessType || "read" }));

      const currentFieldIds = (selectedFields || []).map((f) => f.id);
      const initialSet = new Set((initialFieldIds || []).map(String));
      const currentSet = new Set(currentFieldIds.map(String));
      const addFieldIds = currentFieldIds.filter(
        (fid) => !initialSet.has(String(fid))
      );
      const removeFieldIds = (initialFieldIds || []).filter(
        (fid) => !currentSet.has(String(fid))
      );

      const payload = {
        galleryId: id,
        ...(addCompanies.length ? { addCompanies } : {}),
        ...(removeCompanies.length ? { removeCompanies } : {}),
        ...(addFieldIds.length ? { addFieldIds } : {}),
        ...(removeFieldIds.length ? { removeFieldIds } : {}),
        name,
        description,
        isPublished,
      };

      await updateImageGallery(payload);

      enqueueSnackbar("Gallery saved", {
        variant: "success",
        autoHideDuration: 3000,
      });

      setInitialCompanyDetails((companyDetails || []).map((c) => ({ ...c })));
      setInitialFieldIds([...currentFieldIds]);
    } catch (e) {
      console.error("Update gallery failed", e);
      enqueueSnackbar(e?.message || "Failed to save gallery", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    companyDetails,
    description,
    enqueueSnackbar,
    id,
    name,
    updateImageGallery,
    initialCompanyDetails,
    selectedFields,
    initialFieldIds,
  ]);

  const handleRemoveImage = useCallback(
    (idx) => async () => {
      try {
        const image = galleryImages?.[idx];
        if (!image?.id) return;
        await updateImageGallery({ galleryId: id, removeImageIds: [image.id] });
        const images = await listGalleryImages({ galleryId: id });
        setGalleryImages(Array.isArray(images) ? images : []);
        enqueueSnackbar("Image removed from gallery", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } catch (e) {
        console.error("Error removing image:", e);
        enqueueSnackbar(e?.message || "Failed to remove image", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    },
    [galleryImages, id, enqueueSnackbar]
  );

  /*const openConfirmDelete = useCallback((idx) => {
    setPendingRemoveIndex(idx);
    setConfirmDeleteOpen(true);
  }, []);*/

  const closeConfirmDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
    setPendingRemoveIndex(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingRemoveIndex == null) return;
    try {
      await handleRemoveImage(pendingRemoveIndex)();
    } finally {
      closeConfirmDelete();
    }
  }, [pendingRemoveIndex, handleRemoveImage, closeConfirmDelete]);

  const handleBackToList = useCallback(() => {
    history.push("/image-galleries");
  }, [history]);

  if (isLoading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        p={4}
        width="100%"
      >
        <Typography>Loading gallery...</Typography>
      </Box>
    );
  }

  return (
    <Box boxSizing="border-box" p={4} width="100%">
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Typography variant="h6">Edit Gallery Config</Typography>
          <Typography color="textSecondary" variant="body2">
            ID: {id}
          </Typography>
        </Grid>

        <Grid item>
          <Section>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography gutterBottom variant="subtitle1">
                  History
                </Typography>

                <Timestamps
                  createdAt={createdAt}
                  createdBy={
                    createdBy && typeof createdBy === "object"
                      ? createdBy
                      : { email: createdBy }
                  }
                  dateFormat="dd MMM yyyy HH:mm:ss"
                  isIso={true}
                  journeys={[]} // required but unused here
                  locale="en"
                  updatedAt={updatedAt}
                  updatedBy={
                    updatedBy && typeof updatedBy === "object"
                      ? updatedBy
                      : { email: updatedBy }
                  }
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={name}
                  variant="outlined"
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Autocomplete
                  multiple
                  getOptionLabel={(option) =>
                    option?.name || option?.cname || option?.id || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  loading={isMyAuthCompaniesLoading}
                  options={companiesLocal}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Companies"
                      placeholder="Select one or more companies"
                      variant="outlined"
                    />
                  )}
                  value={companiesLocal.filter((opt) =>
                    (companyDetails || []).some((c) => c.id === opt.id)
                  )}
                  onChange={(e, selectedOptions) => {
                    const updatedDetails = (selectedOptions || []).map(
                      (opt) => {
                        const existing = (companyDetails || []).find(
                          (c) => c.id === opt.id
                        );
                        return {
                          id: opt.id,
                          accessType: existing?.accessType || "read",
                        };
                      }
                    );
                    setCompanyDetails(updatedDetails);
                  }}
                />
              </Grid>

              {fieldsOptions?.length > 0 && (
                <Grid item md={6} xs={12}>
                  <Autocomplete
                    multiple
                    getOptionLabel={(option) =>
                      option?.name || option?.id || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    noOptionsText="No field available"
                    options={fieldsOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Disabled Fields (optional)"
                        placeholder="Select fields to disable for this gallery."
                        variant="outlined"
                      />
                    )}
                    value={selectedFields}
                    onChange={(_, val) => setSelectedFields(val || [])}
                  />
                </Grid>
              )}

              {companyDetails.length > 0 && (
                <Grid item xs={12}>
                  <Typography gutterBottom variant="subtitle1">
                    Company Access Settings
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {companyDetails.map((c, idx) => (
                      <CompanyCard key={c.id}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2">
                              <Chip
                                label={
                                  companiesLocal.find((x) => x.id === c.id)
                                    ?.name ||
                                  companiesLocal.find((x) => x.id === c.id)
                                    ?.cname ||
                                  c.name ||
                                  c.cname ||
                                  c.id
                                }
                                size="small"
                                style={{ marginLeft: 8 }}
                              />
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              select
                              SelectProps={{ native: true }}
                              label="Access Type"
                              size="small"
                              value={c.accessType}
                              variant="outlined"
                              onChange={(e) => {
                                const newDetails = (companyDetails || []).map(
                                  (item, i) =>
                                    i === idx
                                      ? { ...item, accessType: e.target.value }
                                      : item
                                );
                                setCompanyDetails(newDetails);
                              }}
                            >
                              <option value="read">Read</option>
                              <option value="write">Read & Write</option>
                            </TextField>
                          </Grid>
                        </Grid>
                      </CompanyCard>
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  label="Description"
                  minRows={3}
                  value={description}
                  variant="outlined"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPublished}
                      color="primary"
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                  }
                  label="Published"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={8}>
                  <Button
                    color="primary"
                    disabled={isSaving}
                    variant="contained"
                    onClick={handleSaveMeta}
                  >
                    Save
                  </Button>
                  <Button onClick={handleBackToList}>Back to list</Button>
                </Box>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item>
          <Section>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Box alignItems="center" display="flex">
                  {/* Stacked title + subtitle */}
                  <Box display="flex" flexDirection="column">
                    <Typography variant="subtitle1">Images</Typography>
                    <Typography color="textSecondary" variant="body2">
                      Preview of all images available in this gallery.
                    </Typography>
                  </Box>

                  <Box ml={3}>
                    <Tooltip placement="top" title="Edit Images">
                      <span>
                        <IconButton
                          aria-label="edit images"
                          size="small"
                          onClick={() => {
                            const gid = id;
                            modals.openModal(
                              modals.modalTypes.FilteredGalleryImagePreview,
                              {
                                galleryIds: [String(gid)],
                                initialSelectedGalleryIds: [String(gid)],
                              }
                            );
                          }}
                        >
                          <PhotoLibraryIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              <Grid item>
                {isGalleryImagesLoading ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {galleryImages.map((img, idx) => (
                      <Grid
                        key={img?.id || idx}
                        item
                        lg={3}
                        md={4}
                        sm={6}
                        xs={12}
                      >
                        <Paper style={{ padding: 8 }}>
                          <Box
                            alignItems="center"
                            display="flex"
                            height={160}
                            justifyContent="center"
                          >
                            <Img
                              retryOnError
                              container={(children) => (
                                <Box
                                  alignItems="center"
                                  display="flex"
                                  height={160}
                                  justifyContent="center"
                                  width="100%"
                                >
                                  {children}
                                </Box>
                              )}
                              loader={
                                <Box
                                  alignItems="center"
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                  justifyContent="center"
                                  textAlign="center"
                                  width="100%"
                                >
                                  <CircularProgress size={20} />
                                  {previewRetryingById[
                                    String(img?.id || idx)
                                  ] && (
                                    <Typography
                                      color="textSecondary"
                                      variant="caption"
                                    >
                                      File is being scanned. Retrying shortly...
                                    </Typography>
                                  )}
                                </Box>
                              }
                              retryDelayMs={10000}
                              retryMaxAttempts={3}
                              src={[img.url]}
                              style={{
                                width: "100%",
                                height: 160,
                                objectFit: "cover",
                              }}
                              unloader={
                                <Box
                                  alignItems="center"
                                  display="flex"
                                  flexDirection="column"
                                  gap={0.5}
                                  justifyContent="center"
                                  textAlign="center"
                                  width="100%"
                                >
                                  <Typography variant="caption">
                                    Image unavailable
                                  </Typography>
                                  <Typography
                                    color="textSecondary"
                                    variant="caption"
                                  >
                                    Try again in a few moments.
                                  </Typography>
                                </Box>
                              }
                              onRetryStateChange={({ isRetrying }) =>
                                setPreviewRetryingById((prev) => {
                                  const key = String(img?.id || idx);
                                  const next = Boolean(isRetrying);
                                  if (Boolean(prev[key]) === next) return prev;
                                  return { ...prev, [key]: next };
                                })
                              }
                            />
                          </Box>
                          {/*<Box display="flex" justifyContent="flex-end" mt={1}>
                            <Button
                              color="secondary"
                              size="small"
                              onClick={() => openConfirmDelete(idx)}
                            >
                              Remove
                            </Button>
                          </Box>*/}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* Confirm delete image modal */}
        <Dialog
          fullWidth
          maxWidth="xs"
          open={confirmDeleteOpen}
          onClose={closeConfirmDelete}
        >
          <DialogTitle>Remove image from gallery?</DialogTitle>
          <DialogContent>
            <Typography gutterBottom variant="body2">
              This action will delete the image from this gallery.
            </Typography>
            {pendingRemoveIndex != null &&
              galleryImages?.[pendingRemoveIndex] && (
                <Box mt={1}>
                  <img
                    alt="preview-to-remove"
                    src={galleryImages[pendingRemoveIndex].url}
                    style={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                </Box>
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmDelete}>Cancel</Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={confirmDelete}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Box>
  );
};

export default ImageGalleryEditor;
